<template>
  <div class="pt-4">
    <div class="inner py-0">
      <h1>Policy Type Mapping</h1>
    </div>

    <div class="inner flex items-center justify-between gap-4 py-2">
      <div class="flex items-center gap-4">
        <div class="join justify-center">
          <input
            type="radio"
            name="policyTypeOption"
            aria-label="Policy Types"
            class="btn join-item btn-sm"
            @click="mode = 'policyTypes'"
            :checked="mode === 'policyTypes'" />

          <input
            type="radio"
            name="policyTypeOption"
            aria-label="Coverages"
            class="btn join-item btn-sm"
            @click="mode = 'coverages'"
            :checked="mode === 'coverages'" />
          <input
            type="radio"
            name="productOption"
            aria-label="Products"
            class="btn join-item btn-sm"
            @click="mode = 'products'"
            :checked="mode === 'products'" />
        </div>

        <CoverageTypeCreateModal
          v-if="mode === 'coverages'"
          :coverageTypes="distinctCoverageTypes"
          @create="submitCoverageTypeCreate" />
      </div>

      <label class="search-bar input input-sm flex min-w-96 items-center gap-2">
        <input
          ref="searchInputRef"
          type="text"
          placeholder="Search"
          class="grow"
          v-model="filterText"
          autocomplete="off"
          @keyup="handleSearchKeyUp" />
        <XIcon
          v-if="filterText && filterText.length > 0"
          :size="20"
          class="cursor-pointer"
          @click="filterText = ''" />
        <SearchIcon :size="20" />
      </label>
    </div>

    <div class="inner pt-0">
    <!-- Policy Types Tab -->
    <div v-if="mode === 'policyTypes'" class="mt-2">
      <table
        v-tablesort
        id="policy-type-list"
        class="apptable dense">
        <thead>
          <tr>
            <td
              data-test="policyType"
              class="text-left sorted ascending">
              Policy Type
            </td>
            <td
              data-test="description"
              class="description">
              Description
            </td>
            <td data-test="coveragetype">Coverage Type</td>
            <td data-test="coveragetype">Product</td>
            <td class="text-center">Casualty Flag</td>
            <td class="nosort w-20 text-center"></td>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="policyType in sortedPolicyTypes"
            :key="policyType.policyType"
            data-test="row"
            class="group hover:bg-gray-50">
            <td :data-test="`${policyType.policyType}-key`">{{ policyType.policyType }}</td>
            <td>
              <span>{{ policyType.policyDescription }}</span>
            </td>
            <td>
              <span>{{ policyType.coverageType }}</span>
            </td>
            <td>
              <span>{{ policyType.productName }}</span>
            </td>
            <td class="flex justify-center">
              <input
                type="checkbox"
                v-model="policyType.casualtyFlag"
                :checked="policyType.casualtyFlag"
                @change="SetCasualtyFlag(policyType.policyType, $event.target.checked)"
                aria-label="Casualty Flag"
                class="checkbox mx-auto border border-solid border-gray-400 checkbox-sm checkbox-primary" />
            </td>
            <td class="text-center">
              <button
                class="btn opacity-30 btn-ghost transition-opacity btn-sm group-hover:opacity-100"
                @click="handlePolicyTypeEditModalOpen(policyType)"
                :data-test="`${policyType.policyType}-edit`"
                title="Edit policy type">
                <Pencil :size="16" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Coverages Tab -->
    <div
      v-else-if="mode === 'coverages'"
      class="mt-2">
      <table
        id="coverage-type-list"
        class="apptable dense">
        <thead>
          <tr>
            <td class="sorted ascending text-left">Coverage Type</td>
            <td class="sorted ascending text-left">Product</td>
            <td class="sorted ascending text-left">Mapped Prompts</td>
            <td class="nosort w-12 text-center"></td>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="coverageType in sortedCoverageTypes"
            :key="coverageType"
            data-test="coverage-row"
            class="group hover:bg-gray-50">
            <td
              :data-test="`${coverageType}-name`"
              class="text-left">
              {{ coverageType.coverageType }}
            </td>
            <td class="text-left">
              {{ coverageType.productName }}
            </td>
            <td class="text-left">
              <template v-if="coverageType.promptMapping">
                <div
                  v-for="(mapping, index) in coverageType.promptMapping.split(', ')"
                  :key="index"
                  class="whitespace-nowrap">
                  {{ mapping }}
                </div>
              </template>
              <span v-else></span>
            </td>
            <td class="text-center">
              <div class="flex items-center justify-center gap-2">
                <CoverageTypeEditModal
                  :coverageType="coverageType.coverageType"
                  :coverageTypes="distinctCoverageTypes.map((ct) => ct.coverageType)"
                  :products="products"
                  :coverageTypeId="coverageType.coverageTypeId"
                  :productName="coverageType.productName"
                  @updated="refreshData" />
                <button
                  class="btn opacity-30 btn-ghost transition-opacity btn-sm btn-error group-hover:opacity-100"
                  @click="confirmDeleteCoverageType(coverageType.coverageType)"
                  title="Delete coverage type">
                  <Trash2 :size="16" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Products Tab -->
    <div v-else-if="mode === 'products'" class="mt-2">
      <table
        id="products-list"
        class="apptable dense">
        <thead>
          <tr>
            <td class="sorted ascending text-left">Products</td>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="product in filteredProducts"
            :key="product"
            data-test="product-row"
            class="group hover:bg-gray-50">
            <td
              :data-test="`${product}-name`"
              class="text-left">
              {{ product.name }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    </div>

    <!-- Delete Coverage Type Modal -->
    <CoverageTypeDeleteModal
      :show="showDeleteModal"
      :coverage-type="selectedCoverageTypeForDeletion"
      :policy-types="policyTypes"
      @close="onDeleteModalClose"
      @deleted="onCoverageTypeDeleted" />

    <PolicyTypeEditModal
      :show="showPolicyTypeEditModal"
      :policyType="selectedPolicyTypeForEdit"
      :coverageTypes="distinctCoverageTypes.map((ct) => ct.coverageType)"
      @saveDescription="submitDescriptionChange"
      @saveCoverageType="submitCoverageTypeChange"
      @cancelModal="handlePolicyTypeEditModalClose" />
  </div>
</template>
<script>
import { policyTypeService } from '@/services/PolicyTypeService';
import { referenceService } from '@/services/api';
import PolicyTypeEditModal from '@/components/PolicyTypeEditModal.vue';
import CoverageTypeEditModal from '@/components/CoverageTypeEditModal.vue';
import CoverageTypeCreateModal from '@/components/CoverageTypeCreateModal.vue';
import CoverageTypeDeleteModal from '@/components/CoverageTypeDeleteModal.vue';
import { Pencil, Trash2, SearchIcon, XIcon } from '@lucide/vue';

export default {
  name: 'PolicyType',
  components: {
    PolicyTypeEditModal,
    CoverageTypeEditModal,
    CoverageTypeCreateModal,
    CoverageTypeDeleteModal,
    Trash2,
    Pencil,
    SearchIcon,
    XIcon,
  },
  data: () => ({
    loadingData: true,
    error: undefined,
    filterText: '',
    policyTypes: [],
    distinctCoverageTypes: [],
    products: [],
    mode: 'policyTypes',
    showDeleteModal: false,
    showPolicyTypeEditModal: false,
    selectedCoverageTypeForDeletion: '',
    selectedPolicyTypeForEdit: null,
  }),
  computed: {
    sortedPolicyTypes() {
      let result = [...this.policyTypes];

      if (this.filterText) {
        const searchText = this.filterText.toLowerCase();
        result = result.filter(item =>
          item.policyType?.toLowerCase().includes(searchText) ||
          item.policyDescription?.toLowerCase().includes(searchText) ||
          item.coverageType?.toLowerCase().includes(searchText) ||
          item.productName?.toLowerCase().includes(searchText)
        );
      }

      return result.sort((a, b) => {
        const aUndefined = this.isUndefined(a.coverageType);
        const bUndefined = this.isUndefined(b.coverageType);
        if (aUndefined && !bUndefined) return -1;
        if (!aUndefined && bUndefined) return 1;
        return 0;
      });
    },
    sortedCoverageTypes() {
      let result = [...this.distinctCoverageTypes];

      if (this.filterText) {
        const searchText = this.filterText.toLowerCase();
        result = result.filter(item =>
          item.coverageType?.toLowerCase().includes(searchText) ||
          item.productName?.toLowerCase().includes(searchText)
        );
      }

      return result.sort((a, b) => {
        const aUndefined = this.isUndefined(a.productName);
        const bUndefined = this.isUndefined(b.productName);
        if (aUndefined && !bUndefined) return -1;
        if (!aUndefined && bUndefined) return 1;
        return 0;
      });
    },
    filteredProducts() {
      if (!this.filterText) {
        return this.products;
      }

      const searchText = this.filterText.toLowerCase();
      return this.products.filter(item =>
        item.name?.toLowerCase().includes(searchText)
      );
    }
  },
  mounted() {
    if (this.$refs.searchInputRef) {
      this.$refs.searchInputRef.focus();
    }
  },
  methods: {
    handleSearchKeyUp(e) {
      if (e.key && e.key === 'Escape') {
        this.filterText = '';
        e.stopPropagation();
      }
    },
    isUndefined(value) {
      return value === 'Undefined';
    },
    async refreshData() {
      this.loadingData = true;
      this.policyTypes = (await policyTypeService.LoadPolicyTypes()) || [];
      this.products = (await referenceService.products()) || [];
      this.distinctCoverageTypes = ((await policyTypeService.GetDistinctCoverageTypes()) || []).sort((a, b) =>
        a.coverageType.localeCompare(b.coverageType),
      );
      this.loadingData = false;
    },
    handlePolicyTypeEditModalOpen(policyType) {
      this.selectedPolicyTypeForEdit = policyType;
      this.showPolicyTypeEditModal = true;
    },
    handlePolicyTypeEditModalClose() {
      this.showPolicyTypeEditModal = false;
      this.selectedPolicyTypeForEdit = null;
    },
    async submitDescriptionChange(src) {
      await policyTypeService.UpdatePolicyTypeDescription(src);
      this.showPolicyTypeEditModal = false;
      return this.refreshData();
    },
    async submitCoverageTypeChange(policytype, coverageType) {
      let request = { coverageType: coverageType, policyType: policytype.policyType };
      await policyTypeService.UpdatePolicyTypeCoverageType(request);
      this.showPolicyTypeEditModal = false;
      return this.refreshData();
    },
    async submitCoverageTypeRename(oldCoverageType, newCoverageType) {
      let request = { oldName: oldCoverageType, newName: newCoverageType };
      await policyTypeService.RenameCoverageType(request);
      return this.refreshData();
    },
    async SetCasualtyFlag(policyType, value) {
      await policyTypeService.SetCasualtyFlag(policyType, value);
    },
    async submitCoverageTypeEdit(oldCoverageType, newCoverageType) {
      let request = { oldName: oldCoverageType, newName: newCoverageType };
      await policyTypeService.RenameCoverageType(request);
      return this.refreshData();
    },
    async submitCoverageTypeCreate(coverageTypeName) {
      try {
        await policyTypeService.CreateCoverageType(coverageTypeName);
        return this.refreshData();
      } catch (error) {
        console.error('Error creating coverage type:', error);
        // Handle error - could show a toast or alert
        alert('Failed to create coverage type. Please try again.');
      }
    },
    confirmDeleteCoverageType(coverageType) {
      this.selectedCoverageTypeForDeletion = coverageType;
      this.showDeleteModal = true;
    },
    onDeleteModalClose() {
      this.showDeleteModal = false;
      this.selectedCoverageTypeForDeletion = '';
    },
    async onCoverageTypeDeleted() {
      this.showDeleteModal = false;
      this.selectedCoverageTypeForDeletion = '';
      await this.refreshData();
    },
  },
  created() {
    return this.refreshData();
  },
};
</script>
<style scoped>
.flex-td {
  justify-content: space-between;
  display: inline-flex;
  width: 100%;
}

.center-align {
  align-items: center;
  justify-content: center;
}

.description {
  width: 30em;
}
</style>
